import React, { useCallback, useEffect, useState } from 'react'
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Typography,
} from '@mui/material'
import { Helmet } from '@dr.pogodin/react-helmet'
import { Link, useNavigate, useParams } from 'react-router'
import * as datefns from 'date-fns'
import UserApi from '../../../api/UserApi'
import useAuth from '../../../hooks/useAuth'
import useConfirmationDialog from '../../../components/dialogs/ConfirmationDialog'
import UserActionsMenu from '../../../components/entities/UserActionsMenu'
import ConnectCustomerDialog, { ConnectCustomerForm } from './ConnectCustomerDialog'
import PasswordField from './PasswordField'
import { UserCategoryDialog } from './UserCategoryDialog'
import { UserDevicesTable } from './UserDevicesTable'
import {
    UserCategoryType,
    userHasManyDevices,
    UserAdminDetail,
    USER_MULTI_DEVICE_THRESHOLD,
    userCategoryAdminLabel,
} from '@finance-lab/utils/types'

const userApi = new UserApi()

function getApiErrorStatus(error: unknown): number | undefined {
    if (error instanceof Error && 'status' in error && typeof error.status === 'number') {
        return error.status
    }
    return undefined
}

const detailValueSx = {
    variant: 'body2' as const,
    color: 'text.primary',
    sx: { display: 'block', lineHeight: 1.5, minHeight: '1.5em' },
}

function DetailField({
    label,
    value,
    linkTo,
    mailto,
    monospace,
}: {
    label: string
    value: string
    linkTo?: string
    mailto?: boolean
    monospace?: boolean
}) {
    const valueTypographyProps = {
        ...detailValueSx,
        sx: {
            ...detailValueSx.sx,
            ...(monospace ? { fontFamily: 'monospace', fontSize: '0.8125rem', wordBreak: 'break-all' } : {}),
        },
    }

    return (
        <Box>
            <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 0.5 }}>
                {label}
            </Typography>
            {linkTo ? (
                <Typography {...valueTypographyProps} component={Link} to={linkTo} color='primary'>
                    {value}
                </Typography>
            ) : mailto ? (
                <Typography
                    {...valueTypographyProps}
                    component='a'
                    href={`mailto:${value}`}
                    color='primary'
                    sx={{
                        ...valueTypographyProps.sx,
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' },
                    }}
                >
                    {value}
                </Typography>
            ) : (
                <Typography {...valueTypographyProps}>{value}</Typography>
            )}
        </Box>
    )
}

function AdminUserDetailsView() {
    const { userId } = useParams()
    const navigate = useNavigate()
    const { isAuthenticated } = useAuth()
    const { confirm, ConfirmationDialog } = useConfirmationDialog()

    const [user, setUser] = useState<UserAdminDetail | null>(null)
    const [loading, setLoading] = useState(true)
    const [notFound, setNotFound] = useState(false)
    const [loadError, setLoadError] = useState<string | null>(null)
    const [connectCustomerDialogOpen, setConnectCustomerDialogOpen] = useState(false)
    const [userCategoryDialogOpen, setUserCategoryDialogOpen] = useState(false)
    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false)
    const [newPassword, setNewPassword] = useState('')

    const loadUser = useCallback(async () => {
        if (!userId || !isAuthenticated) {
            return
        }
        setLoading(true)
        setNotFound(false)
        setLoadError(null)
        try {
            const detail = await userApi.adminGetUser(userId)
            setUser(detail)
        } catch (error) {
            setUser(null)
            if (getApiErrorStatus(error) === 404) {
                setNotFound(true)
            } else {
                setLoadError('Kunde inte ladda användaren.')
            }
        } finally {
            setLoading(false)
        }
    }, [userId, isAuthenticated])

    useEffect(() => {
        void loadUser()
    }, [loadUser])

    const handleDeleteUser = async (id: string) => {
        const confirmation = await confirm('Är du säker på att du vill ta bort användaren?')
        if (!confirmation) return
        await userApi.adminDeleteUser(id)
        void navigate('/admin/users')
    }

    const handleDisconnectClick = async () => {
        if (!user) return
        const confirmation = await confirm('Är du säker på att du vill koppla bort användaren från kunden?')
        if (!confirmation) return
        await userApi.disconnectFromCustomer(user.id)
        await loadUser()
    }

    const handleCustomerSubmit = async (data: ConnectCustomerForm) => {
        if (!user || !data.customer) return
        await userApi.connectToCustomer(user.id, data.customer.id)
        await loadUser()
        setConnectCustomerDialogOpen(false)
    }

    const handleUserCategorySave = async (
        newCategory: UserCategoryType,
        trialPeriod?: { startsAt: Date; endsAt: Date },
    ) => {
        if (!user) return
        await userApi.adminSetUserCategory(user.id, newCategory, trialPeriod)
        await loadUser()
    }

    const handleChangePassword = async () => {
        if (!user || !newPassword) {
            alert('Ange ett nytt lösenord.')
            return
        }
        try {
            await userApi.adminSetUserPassword(user.id, newPassword)
            alert('Lösenordet har ändrats.')
            setPasswordDialogOpen(false)
            setNewPassword('')
        } catch {
            alert('Kunde inte ändra lösenord.')
        }
    }

    if (loading) {
        return (
            <Typography sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}>
                <CircularProgress size={24} />
                Laddar användare…
            </Typography>
        )
    }

    if (notFound) {
        return (
            <>
                <Typography variant='h5' gutterBottom>
                    Användaren hittades inte
                </Typography>
                <Button component={Link} to='/admin/users' variant='outlined'>
                    Tillbaka till listan
                </Button>
            </>
        )
    }

    if (loadError) {
        return (
            <>
                <Alert severity='error' sx={{ mb: 3 }}>
                    {loadError}
                </Alert>
                <Button variant='outlined' onClick={() => void loadUser()} sx={{ mr: 2 }}>
                    Försök igen
                </Button>
                <Button component={Link} to='/admin/users' variant='text'>
                    Tillbaka till listan
                </Button>
            </>
        )
    }

    if (!user) {
        return null
    }

    const displayName = user.name || user.email

    return (
        <>
            <Helmet title={`Användare: ${displayName}`} />
            <ConfirmationDialog />
            <Box sx={{ mb: 4, width: '100%' }}>
                <Button component={Link} to='/admin/users' size='small' sx={{ mb: 2 }}>
                    ← Användare
                </Button>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant='h3' gutterBottom sx={{ flex: 1, minWidth: 0 }}>
                        {displayName}
                    </Typography>
                    <Box sx={{ flexShrink: 0 }}>
                        <UserActionsMenu
                            user={user}
                            onSetUserCategory={() => setUserCategoryDialogOpen(true)}
                            onDisconnectClick={user.customerName ? () => void handleDisconnectClick() : undefined}
                            onDeleteUser={(id) => void handleDeleteUser(id)}
                            onChangePassword={() => setPasswordDialogOpen(true)}
                        />
                    </Box>
                </Box>
            </Box>

            {!user.customerName && (
                <Button
                    variant='outlined'
                    size='small'
                    sx={{ mb: 4 }}
                    onClick={() => setConnectCustomerDialogOpen(true)}
                >
                    Koppla till kund
                </Button>
            )}

            {userHasManyDevices(user.deviceCount ?? 0) && (
                <Alert severity='info' sx={{ mb: 4 }}>
                    {user.deviceCount} registrerade enheter totalt (varning vid fler än {USER_MULTI_DEVICE_THRESHOLD})
                </Alert>
            )}

            <Card sx={{ mb: 6 }}>
                <CardContent>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                            gap: 3,
                            columnGap: 4,
                        }}
                    >
                        <DetailField label='E-post' value={user.email} mailto />
                        <DetailField label='Cognito Username' value={user.username} monospace />
                        <DetailField label='Telefon' value={user.phoneNumber || '—'} />
                        <DetailField label='Kategori' value={userCategoryAdminLabel(user.userCategory)} />
                        {user.userCategory === 'trial' && (
                            <DetailField
                                label='Testperiod'
                                value={
                                    user.trialPeriod
                                        ? `${datefns.format(new Date(user.trialPeriod.startsAt), 'yyyy-MM-dd')} – ${datefns.format(new Date(user.trialPeriod.endsAt), 'yyyy-MM-dd')}`
                                        : '—'
                                }
                            />
                        )}
                        <DetailField
                            label='Kund'
                            value={user.customerName || '—'}
                            linkTo={
                                user.customerName && user.customerId ? `/admin/customers/${user.customerId}` : undefined
                            }
                        />
                        <DetailField
                            label='Senast aktiv'
                            value={
                                user.lastActive ? datefns.format(new Date(user.lastActive), 'yyyy-MM-dd HH:mm') : '—'
                            }
                        />
                        <DetailField
                            label='Skapad'
                            value={user.createdAt ? datefns.format(new Date(user.createdAt), 'yyyy-MM-dd HH:mm') : '—'}
                        />
                    </Box>
                </CardContent>
            </Card>

            <Divider sx={{ mb: 4 }} />

            <Typography variant='h5' gutterBottom>
                Enheter ({user.deviceCount ?? 0})
            </Typography>
            <Card>
                <CardContent>
                    <UserDevicesTable devices={user.devices ?? []} />
                </CardContent>
            </Card>

            <ConnectCustomerDialog
                open={connectCustomerDialogOpen}
                onClose={() => setConnectCustomerDialogOpen(false)}
                onSubmit={handleCustomerSubmit}
            />

            <UserCategoryDialog
                open={userCategoryDialogOpen}
                onClose={() => setUserCategoryDialogOpen(false)}
                userEmail={user.email}
                initialUserCategory={user.userCategory}
                initialTrialPeriod={user.trialPeriod}
                onSave={handleUserCategorySave}
            />

            <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)}>
                <DialogTitle>Ändra lösenord för {displayName}</DialogTitle>
                <DialogContent>
                    <PasswordField
                        label='Nytt lösenord'
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        autoFocus
                        showGeneratePassword
                        sx={{ my: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPasswordDialogOpen(false)} color='secondary'>
                        Avbryt
                    </Button>
                    <Button onClick={() => void handleChangePassword()} color='primary'>
                        Ändra
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default AdminUserDetailsView
